import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { 
  Settings as SettingsIcon, 
  Save,
  Building,
  User,
  Mail,
  Phone,
  MapPin,
  Globe,
  Bell,
  Shield,
  CreditCard,
  Database,
  CheckCircle,
  AlertTriangle
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/form-input'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'

const CollegeSettings = () => {
  const [activeTab, setActiveTab] = useState('college')
  const [collegeInfo, setCollegeInfo] = useState({
    name: 'Adarsha University Engineering College',
    code: 'AUEC',
    address: '123 University Road, Tech City',
    phone: '+91-9876543210',
    email: 'admin@auec.edu.in',
    website: 'https://auec.edu.in',
    description: 'Leading engineering college providing quality education in various technical disciplines.'
  })

  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    feeReminders: true,
    systemAlerts: true,
    paymentConfirmations: true
  })

  const [paymentSettings, setPaymentSettings] = useState({
    lateFeePercentage: 5,
    gracePeriodDays: 7,
    allowPartialPayments: true,
    minimumPartialAmount: 1000
  })

  const tabs = [
    { id: 'college', label: 'College Information', icon: Building },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'payments', label: 'Payment Settings', icon: CreditCard },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'system', label: 'System Status', icon: Database }
  ]

  const handleCollegeInfoChange = (field: string, value: string) => {
    setCollegeInfo(prev => ({ ...prev, [field]: value }))
  }

  const handleNotificationChange = (field: string, value: boolean) => {
    setNotificationSettings(prev => ({ ...prev, [field]: value }))
  }

  const handlePaymentChange = (field: string, value: string | boolean | number) => {
    setPaymentSettings(prev => ({ ...prev, [field]: value }))
  }

  const saveSettings = () => {
    // Implement save functionality
    console.log('Saving settings...')
  }

  const renderCollegeSettings = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Building className="h-5 w-5 text-green-600" />
            <span>College Information</span>
          </CardTitle>
          <CardDescription>
            Manage your college's basic information and contact details
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                College Name
              </label>
              <Input
                value={collegeInfo.name}
                onChange={(e) => handleCollegeInfoChange('name', e.target.value)}
                placeholder="Enter college name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                College Code
              </label>
              <Input
                value={collegeInfo.code}
                onChange={(e) => handleCollegeInfoChange('code', e.target.value)}
                placeholder="Enter college code"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Address
            </label>
            <Textarea
              value={collegeInfo.address}
              onChange={(e) => handleCollegeInfoChange('address', e.target.value)}
              placeholder="Enter college address"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <Input
                value={collegeInfo.phone}
                onChange={(e) => handleCollegeInfoChange('phone', e.target.value)}
                placeholder="+91-9876543210"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <Input
                type="email"
                value={collegeInfo.email}
                onChange={(e) => handleCollegeInfoChange('email', e.target.value)}
                placeholder="admin@college.edu"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Website
              </label>
              <Input
                value={collegeInfo.website}
                onChange={(e) => handleCollegeInfoChange('website', e.target.value)}
                placeholder="https://college.edu"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <Textarea
              value={collegeInfo.description}
              onChange={(e) => handleCollegeInfoChange('description', e.target.value)}
              placeholder="Brief description of the college"
              rows={4}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  )

  const renderNotificationSettings = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Bell className="h-5 w-5 text-green-600" />
            <span>Notification Preferences</span>
          </CardTitle>
          <CardDescription>
            Configure how you want to receive notifications
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {Object.entries(notificationSettings).map(([key, value]) => (
            <div key={key} className="flex items-center justify-between p-4 rounded-lg border">
              <div>
                <h4 className="font-medium text-gray-900">
                  {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                </h4>
                <p className="text-sm text-gray-500">
                  {key === 'emailNotifications' && 'Receive notifications via email'}
                  {key === 'smsNotifications' && 'Receive notifications via SMS'}
                  {key === 'feeReminders' && 'Get reminders for fee due dates'}
                  {key === 'systemAlerts' && 'Receive system maintenance and update alerts'}
                  {key === 'paymentConfirmations' && 'Get confirmations for successful payments'}
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={value}
                  onChange={(e) => handleNotificationChange(key, e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
              </label>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  )

  const renderPaymentSettings = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <CreditCard className="h-5 w-5 text-green-600" />
            <span>Payment Configuration</span>
          </CardTitle>
          <CardDescription>
            Configure payment policies and fee structures
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Late Fee Percentage (%)
              </label>
              <Input
                type="number"
                value={paymentSettings.lateFeePercentage}
                onChange={(e) => handlePaymentChange('lateFeePercentage', Number(e.target.value))}
                placeholder="5"
                min="0"
                max="50"
              />
              <p className="text-xs text-gray-500 mt-1">
                Percentage to charge as late fee on overdue payments
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Grace Period (Days)
              </label>
              <Input
                type="number"
                value={paymentSettings.gracePeriodDays}
                onChange={(e) => handlePaymentChange('gracePeriodDays', Number(e.target.value))}
                placeholder="7"
                min="0"
                max="30"
              />
              <p className="text-xs text-gray-500 mt-1">
                Number of days before applying late fees
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 rounded-lg border">
            <div>
              <h4 className="font-medium text-gray-900">Allow Partial Payments</h4>
              <p className="text-sm text-gray-500">
                Enable students to make partial fee payments
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={paymentSettings.allowPartialPayments}
                onChange={(e) => handlePaymentChange('allowPartialPayments', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
            </label>
          </div>

          {paymentSettings.allowPartialPayments && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Minimum Partial Payment Amount (₹)
              </label>
              <Input
                type="number"
                value={paymentSettings.minimumPartialAmount}
                onChange={(e) => handlePaymentChange('minimumPartialAmount', Number(e.target.value))}
                placeholder="1000"
                min="100"
              />
              <p className="text-xs text-gray-500 mt-1">
                Minimum amount required for partial payments
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )

  const renderSecuritySettings = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Shield className="h-5 w-5 text-green-600" />
            <span>Security Settings</span>
          </CardTitle>
          <CardDescription>
            Manage security policies and access controls
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 rounded-lg border border-green-200 bg-green-50">
              <div className="flex items-center space-x-2 mb-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="font-medium text-green-800">Password Policy</span>
              </div>
              <p className="text-sm text-green-700">
                Strong password requirements enabled
              </p>
            </div>
            <div className="p-4 rounded-lg border border-green-200 bg-green-50">
              <div className="flex items-center space-x-2 mb-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="font-medium text-green-800">Two-Factor Auth</span>
              </div>
              <p className="text-sm text-green-700">
                2FA enabled for admin accounts
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">Access Control</h4>
            <div className="space-y-3">
              {[
                'Student data access logged',
                'Payment processing encrypted',
                'Session timeout: 30 minutes',
                'Failed login attempts tracked'
              ].map((item, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )

  const renderSystemStatus = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Database className="h-5 w-5 text-green-600" />
            <span>System Status</span>
          </CardTitle>
          <CardDescription>
            Monitor system health and performance
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { service: 'Database', status: 'Connected', color: 'green' },
              { service: 'Payment Gateway', status: 'Active', color: 'green' },
              { service: 'Email Service', status: 'Active', color: 'green' },
              { service: 'Backup System', status: 'Warning', color: 'yellow' }
            ].map((item, index) => (
              <div key={index} className="flex items-center justify-between p-4 rounded-lg border">
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full bg-${item.color}-500`}></div>
                  <div>
                    <p className="font-medium text-gray-900">{item.service}</p>
                    <p className={`text-sm text-${item.color}-600`}>{item.status}</p>
                  </div>
                </div>
                {item.color === 'yellow' && (
                  <AlertTriangle className="h-5 w-5 text-yellow-600" />
                )}
                {item.color === 'green' && (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )

  const renderContent = () => {
    switch (activeTab) {
      case 'college':
        return renderCollegeSettings()
      case 'notifications':
        return renderNotificationSettings()
      case 'payments':
        return renderPaymentSettings()
      case 'security':
        return renderSecuritySettings()
      case 'system':
        return renderSystemStatus()
      default:
        return renderCollegeSettings()
    }
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600">Configure college preferences and system settings</p>
        </div>
        
        <Button onClick={saveSettings} className="flex items-center bg-green-600 hover:bg-green-700">
          <Save className="h-4 w-4" />
          <span>Save Changes</span>
        </Button>
      </div>

      {/* Settings Navigation */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                </button>
              )
            })}
          </div>
        </CardContent>
      </Card>

      {/* Settings Content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {renderContent()}
      </motion.div>
    </div>
  )
}

export default CollegeSettings
